import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { APP } from '@/constants/testIds';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Edit3, ShieldCheck, Ban, Lock } from 'lucide-react';

const STATUSES = ['all', 'new', 'known', 'old', 'blocked', 'suspicious'];
const statusPill = {
  new: 'pill-medium',
  known: 'pill-low',
  old: 'pill-high',
  blocked: 'pill-critical',
  suspicious: 'pill-critical',
};

const IPTracker = () => {
  const { user } = useAuth();
  const isPaid = user?.payment_status === 'paid' || user?.role === 'admin';
  const [devices, setDevices] = useState([]);
  const [status, setStatus] = useState('all');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get('/devices');
    setDevices(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const update = async (id, patch, msg) => {
    try {
      await api.patch(`/devices/${id}`, patch);
      toast.success(msg);
      load();
    } catch (e) { toast.error('Action failed'); }
  };

  const rename = (d) => {
    const v = window.prompt('Rename device', d.custom_name || d.name);
    if (v && v.trim()) update(d.device_id, { custom_name: v.trim() }, 'Device renamed');
  };

  const filtered = status === 'all' ? devices : devices.filter((d) => d.status === status);

  return (
    <div className="space-y-8">
      <header>
        <div className="label-tag mb-2">// IP Tracker</div>
        <h1 className="text-4xl md:text-5xl font-light tracking-tight">Device inventory</h1>
        <p className="text-[color:var(--text-secondary)] mt-2 max-w-2xl">
          Every device ever observed on your network — searchable by status, with rename, trust, and block actions.
        </p>
      </header>

      {!isPaid && (
        <div data-testid="payment-required-banner-iptracker" className="panel p-5 border-[color:var(--status-medium)] bg-[rgba(255,179,2,0.06)] flex items-start gap-4">
          <Lock className="text-[color:var(--status-medium)] mt-1" size={18} />
          <div className="flex-1">
            <div className="label-tag text-[color:var(--status-medium)] mb-1">// IP &amp; MAC Addresses Masked</div>
            <p className="text-sm text-[color:var(--text-secondary)] leading-relaxed">
              You&apos;re viewing a redacted preview. Full IPs (e.g. <span className="font-mono text-white">192.168.1.34</span>)
              and MAC addresses become visible the moment a Supreme Web Security admin marks your account as
              <span className="text-[color:var(--status-low)] font-mono"> PAID</span>.
            </p>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <span className="label-tag mr-2">Filter:</span>
        {STATUSES.map((s) => (
          <button
            key={s}
            data-testid={`${APP.ipFilterStatus}-${s}`}
            onClick={() => setStatus(s)}
            className={`pill ${status === s ? 'pill-brand' : 'pill-muted'} uppercase`}
          >
            {s}
          </button>
        ))}
      </div>

      <div className="panel overflow-x-auto">
        <table data-testid={APP.ipTrackerTable} className="dtable min-w-[1000px]">
          <thead>
            <tr>
              <th>IP</th><th>Device</th><th>MAC</th><th>Vendor</th><th>Conn</th><th>First Seen</th><th>Last Seen</th><th>Status</th><th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="text-[color:var(--text-muted)] blink">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={9} className="text-[color:var(--text-secondary)]">No devices match this filter. Run a scan to populate inventory.</td></tr>
            ) : (
              filtered.map((d) => (
                <tr key={d.device_id} data-testid={APP.deviceRow}>
                  <td className="text-[color:var(--brand)]">{d.ip}</td>
                  <td>{d.custom_name || d.name}</td>
                  <td className="text-[color:var(--text-secondary)]">{d.mac}</td>
                  <td>{d.vendor}</td>
                  <td className="uppercase text-[color:var(--text-secondary)]">{d.connection}</td>
                  <td className="text-[color:var(--text-secondary)]">{new Date(d.first_seen).toLocaleDateString()}</td>
                  <td className="text-[color:var(--text-secondary)]">{new Date(d.last_seen).toLocaleString()}</td>
                  <td><span className={`pill ${statusPill[d.status] || 'pill-muted'}`}>{d.status}</span></td>
                  <td className="text-right">
                    <div className="inline-flex gap-1">
                      <button data-testid={APP.deviceRenameBtn} onClick={() => rename(d)} className="p-1.5 border border-[color:var(--border)] hover:border-[color:var(--brand)] hover:text-[color:var(--brand)]" title="Rename">
                        <Edit3 size={12} />
                      </button>
                      <button data-testid={APP.deviceTrustBtn} onClick={() => update(d.device_id, { trusted: true }, 'Marked as trusted')} className="p-1.5 border border-[color:var(--border)] hover:border-[color:var(--status-low)] hover:text-[color:var(--status-low)]" title="Trust">
                        <ShieldCheck size={12} />
                      </button>
                      <button data-testid={APP.deviceBlockBtn} onClick={() => update(d.device_id, { blocked: true }, 'Device blocked')} className="p-1.5 border border-[color:var(--border)] hover:border-[color:var(--status-critical)] hover:text-[color:var(--status-critical)]" title="Block">
                        <Ban size={12} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default IPTracker;
